import { isTokenValid } from '@client-shared/utils/token-validation'
import { CanceledError } from 'axios'

import sendRaygun from '@/utils/send-raygun.js'

// The refresh that is currently in flight, shared by every request that arrives while it runs.
//
// This used to be a plain `isRefreshingToken` boolean that callers checked and then SKIPPED the
// wait on. That meant only the first request of a burst waited for the new token: every other
// request went out with the token that had just been found expired, and the server rejected those
// with 401. was-sent-with-stale-access-token.js usually keeps that from ending the session, but
// only once the refresh has actually landed -- a 401 that comes back before it does still matches
// the current token and logs the user out.
//
// Sharing the promise makes the whole burst queue behind the one refresh instead, so they all
// reach set-headers.js (which runs after this interceptor) with the new token.
// 🚨 Has to match ionsrc/plugins/axios-interceptors/auth-handler.js
let refreshPromise

// Whether the route the request was created on depends on the session. A public or sharing route does
// not: /confirm/ and the download route are reached with dead tokens routinely and must still work, so
// neither the logout nor the abandon below may fire there.
const isSessionRoute = (app) => {
  const currentRoute = app.config.globalProperties.$router.resolve(window.location.pathname)

  return currentRoute.meta.layout !== 'public' && currentRoute.meta.layout !== 'sharing'
}

// Set while the logout of an expired session (below) runs. That logout clears the tokens the moment it
// starts, so a request created before it has navigated away would skip the expiry branch and go out
// unauthenticated: a 401, instead of the quiet cancel the rest of the burst gets. Not a shared promise
// to await: the logout's own navigation to /login runs the route guard, whose current-user request would
// then wait on the logout it is part of.
let isEndingExpiredSession = false

export default ({
  $axios,
  app,
}) => {
  $axios.interceptors.request.use(async (config) => {
    const $auth = app.config.globalProperties.$auth

    /**
     * Auth token expiry interceptor
     * check access token and refresh token for expiry and refresh if expired
     */
    if (config.url.includes('refresh-token')) {
      return config
    }

    if (isEndingExpiredSession && isSessionRoute(app)) {
      throw new CanceledError('TOKEN_REFRESH_FAILED')
    }

    const accessToken = $auth.accessToken
    const refreshToken = $auth.refreshToken
    const userId = $auth.user?._id
    const sessionGeneration = $auth.sessionGeneration

    if (
      accessToken &&
      refreshToken &&
      !isTokenValid(accessToken)
    ) {
      // Resolved BEFORE the await: a failed refresh logs out, and the logout navigates to /login, which
      // is a public route. Asking afterwards would answer "public" for a request created on a session
      // route and let it through after all.
      const startedOnSessionRoute = isSessionRoute(app)

      // The refresh token has run out as well (70 days on the web, see servsrc/lib/auth/token.service.js),
      // so the server can only answer "Token is expired. Subject: refresh_token". Settle it as a failed
      // refresh without the round trip and without the token-refresh-failed report: it is the expected
      // end of an abandoned session - typically someone coming back through an email link months later -
      // not a fault. A refresh already in flight is still awaited, its outcome is the honest one.
      //
      // Offset 0 rather than the access-token default: a refresh token that is about to expire still
      // exchanges fine, so only one that is past its exp is given up on.
      const isRefreshTokenExpired = !refreshPromise && !isTokenValid(refreshToken, 0)

      if (isRefreshTokenExpired && startedOnSessionRoute) {
        isEndingExpiredSession = true

        $auth.logout()
          .finally(() => {
            isEndingExpiredSession = false
          })
      }

      if (!refreshPromise && !isRefreshTokenExpired) {
        // Resolved to whether the refresh worked rather than rejecting, so one failure does not reject
        // the whole queue with an error every caller would have to understand. Cleared on settle so the
        // next expiry refreshes again.
        refreshPromise = $auth.getNewRefreshToken()
          .then(() => true)
          .catch(async (error) => {
            // A rotation on the wire replaced the refresh token this refresh used: its failure says nothing about
            // the session the rotation leaves behind
            await $auth.waitForSentTokenRotation()

            if ($auth.refreshToken !== refreshToken) {
              return false
            }

            await sendRaygun({
              error,
              tags: [ 'token-refresh-failed' ],
            })

            // only logout if route is not public, otherwise just stay on page
            if (isSessionRoute(app)) {
              $auth.logout()
            }

            return false
          })
          .finally(() => {
            refreshPromise = undefined
          })
      }

      const didRefresh = !isRefreshTokenExpired && await refreshPromise

      // A rotation on the wire (2FA confirm, password change) replaces the tokens the failed refresh was about:
      // carry on with the rotated pair instead of abandoning the request (see pendingTokenRotation in the auth
      // plugin). The rotation request itself never waits here - it waits for this refresh in turn. A rotation that
      // already finished is no longer pending, so the refresh token tells it too: it differs from the one this
      // request started with. An ended session has none left, so it never carries on - and the session must still be
      // the one the request was made for: after a logout and another login, even to the same account, it must not run.
      const isReplacedByRotation = !didRefresh && !config.isTokenRotation && Boolean(userId) && $auth.sessionGeneration === sessionGeneration && (
        await $auth.waitForSentTokenRotation() ||
        (Boolean($auth.refreshToken) && $auth.refreshToken !== refreshToken)
      ) && $auth.sessionGeneration === sessionGeneration

      // The refresh failed, so there is no usable token and the session is ending. Abandon the request
      // instead of putting a token we KNOW is dead on the wire: the server answers 401, and that 401 is
      // indistinguishable from a real session end, which is the confusion
      // was-sent-with-stale-access-token.js exists to paper over. Cancelling rather than throwing keeps
      // it quiet - error-handler.js drops cancels and raygun.js never reports them.
      if (!didRefresh && !isReplacedByRotation && startedOnSessionRoute) {
        throw new CanceledError('TOKEN_REFRESH_FAILED')
      }

      // A public route keeps its request - /confirm/ and the download route are reached with dead
      // tokens routinely - but it must not carry the dead token while it goes. Handing set-headers.js
      // an explicit null makes it drop the header (see the branch there), so the request leaves
      // unauthenticated instead of presenting credentials the server will reject and we already know
      // are finished. This is the path a request created after the logout navigation takes: by then
      // window.location is /login, which is public.
      if (!didRefresh && !isReplacedByRotation && !startedOnSessionRoute) {
        config.headers.Authorization = null
      }

      // Whatever the refresh did, do NOT re-derive it from isTokenValid($auth.accessToken) here. A
      // token minted seconds ago still reads as expired to a browser whose clock runs ahead, so that
      // check calls a perfectly good refresh a failure and would strand every request on a device with
      // a fast clock. The refresh's own outcome is the only honest signal - getNewRefreshToken rejects
      // when it fails, which is what the catch above is for.
      // cypress/e2e/IONIC/_features/auth-refresh-token.spec.js documents the same trap from the test
      // side: under a ticked clock the replacement token reads as expired too, so the burst refreshes
      // again on every request.
    }

    // From here on the rotation request is on the wire: 401s and failed refreshes may wait for it now
    if (config.isTokenRotation) {
      $auth.markTokenRotationRequestSent()
    }

    return config
  })
}
